import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GenerateEventTypesResult,
  ManagedEvent,
  generateManagedEventTypes,
} from "@/features/supply-config/managed-events-api";
import {
  getEventTypesByManagedEvent,
  upsertRoundRobinEventType,
} from "@/features/supply-config/event-types-api";
import {
  StagingEventType,
  getStagingEventTypes,
} from "@/features/supply-config/calendly-api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/features/forms/ui/bundles-multi-select";
import { toast } from "@/hooks/use-toast";

/** Lowercase + hyphenate free text into a stable slug (mirrors product-config). */
const slugify = (v: string) =>
  v
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Configure the EventTypes under a ManagedEvent: generate the individual
 * per-professional event types (across all its teams), and set up round-robin
 * events — each identified by a free-text `type` slug (unique per managed event),
 * mapped to one or more teams.
 */
export function ConfigureManagedEventDialog({
  managedEvent,
  open,
  onOpenChange,
}: {
  managedEvent: ManagedEvent | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const meId = managedEvent?.id;
  const teams = managedEvent?.teams ?? [];

  const { data: eventTypes = [] } = useQuery({
    queryKey: ["event-types", "by-managed", meId],
    queryFn: () => getEventTypesByManagedEvent(meId!),
    enabled: !!meId && open,
  });
  const roundRobinEvents = eventTypes.filter((e) => e.teams?.length);

  const [search, setSearch] = useState("");
  const [typeSlug, setTypeSlug] = useState("");
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [genResult, setGenResult] = useState<GenerateEventTypesResult | null>(
    null,
  );
  // On open: default the picker search to the name; pre-select all the managed
  // event's teams (a round-robin event can span several of them).
  useEffect(() => {
    if (open && managedEvent) {
      setSearch(managedEvent.name ?? "");
      setTypeSlug("");
      setSelectedTeamIds((managedEvent.teams ?? []).map((t) => t.id));
      setGenResult(null);
    }
  }, [open, managedEvent?.id]);

  // A round-robin's `type` slug must be unique within this managed event —
  // validate client-side against the already-configured round-robins (mirrors
  // the onboarding-form slug check in product-config).
  const typeIsDuplicate =
    !!typeSlug.trim() &&
    roundRobinEvents.some(
      (e) => (e.type ?? "").trim() && slugify(e.type!) === slugify(typeSlug),
    );

  const { data: stagingResult } = useQuery({
    queryKey: ["staging-round-robin", search],
    queryFn: () =>
      getStagingEventTypes({ type: "round_robin", search, page: 0, limit: 25 }),
    enabled: open && !!managedEvent?.round_robin_pool_active,
  });
  const candidates = stagingResult?.eventTypes ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["event-types"] });

  const generate = useMutation({
    mutationFn: () => generateManagedEventTypes(meId!),
    onSuccess: (res) => {
      invalidate();
      setGenResult(res);
      toast({
        title: `Created ${res.created} event type${res.created === 1 ? "" : "s"}`,
        description: `${res.matched}/${res.totalProfessionals} professionals matched`,
      });
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  const createRR = useMutation({
    mutationFn: (row: StagingEventType) =>
      upsertRoundRobinEventType({
        managed_event_id: meId!,
        team_ids: selectedTeamIds,
        calendly_event_type_uri: row.calendly_uri,
        scheduling_url: row.scheduling_url ?? undefined,
        type: slugify(typeSlug),
        is_active: row.active ?? true,
      }),
    onSuccess: () => {
      invalidate();
      setTypeSlug("");
      toast({ title: "Round-robin event type saved" });
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure · {managedEvent?.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <Badge variant="outline">{managedEvent?.category}</Badge>
            <span>Teams: {teams.map((t) => t.name).join(", ") || "—"}</span>
            <span>· Attendees: {managedEvent?.participant_scope}</span>
            {managedEvent?.round_robin_pool_active && (
              <Badge variant="outline">round-robin pool</Badge>
            )}
          </div>

          {/* Configured summary (full lists live on the details page) */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Badge variant="outline">{roundRobinEvents.length} round-robin</Badge>
            <Badge variant="outline">
              {eventTypes.filter((e) => e.professional_id).length} individual
            </Badge>
            <span>configured</span>
          </div>

          {/* Individual per-professional event types */}
          <div className="space-y-2 border-t border-gray-100 pt-4">
            <p className="text-xs font-medium text-gray-500">
              Individual professional event types
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                disabled={generate.isPending}
                onClick={() => generate.mutate()}
              >
                {generate.isPending
                  ? "Generating…"
                  : "Generate for all professionals"}
              </Button>
              <span className="text-[11px] text-gray-400">
                One event type per professional across all teams, matched from
                staged Calendly solo events by name + owner.
              </span>
            </div>
            {genResult && (
              <div className="text-xs text-gray-600 rounded-md border border-gray-200 p-2 space-y-1">
                <p>
                  Created <strong>{genResult.created}</strong>, skipped{" "}
                  {genResult.skipped} (already linked). Matched{" "}
                  {genResult.matched}/{genResult.totalProfessionals}{" "}
                  professionals.
                </p>
                {genResult.unmatched.length > 0 && (
                  <details>
                    <summary className="cursor-pointer text-amber-600">
                      {genResult.unmatched.length} unmatched
                    </summary>
                    <ul className="mt-1 space-y-0.5 pl-1">
                      {genResult.unmatched.map((u) => (
                        <li key={u.id}>
                          {u.name} — {u.reason}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>

          {/* Round-robin setup — one event mapped to 1+ teams, per `type` slug */}
          {managedEvent?.round_robin_pool_active ? (
            <div className="space-y-2 border-t border-gray-100 pt-4">
              <p className="text-xs font-medium text-gray-500">
                Round-robin event types (mapped to one or more teams)
              </p>
              {teams.length === 0 ? (
                <p className="text-xs text-amber-600">
                  This managed event has no teams.
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <MultiSelect
                        options={teams.map((t) => ({
                          label: t.name,
                          value: t.id,
                        }))}
                        value={selectedTeamIds}
                        onChange={(v) => setSelectedTeamIds(v)}
                        placeholder="Teams for this round-robin"
                      />
                    </div>
                    <Input
                      value={typeSlug}
                      onChange={(e) => setTypeSlug(e.target.value)}
                      onBlur={(e) => setTypeSlug(slugify(e.target.value))}
                      placeholder="Type (unique slug)"
                      className="h-8 w-40 text-sm"
                    />
                  </div>
                  {typeIsDuplicate && (
                    <p className="text-[11px] text-red-500">
                      Type must be unique across this managed event&apos;s
                      round-robins.
                    </p>
                  )}

                  {selectedTeamIds.length === 0 ? (
                    <p className="text-xs text-amber-600">
                      Select at least one team.
                    </p>
                  ) : (
                    <>
                      <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search round-robin events…"
                        className="h-8 text-sm"
                      />
                      <div className="border border-gray-200 rounded-md max-h-56 overflow-y-auto divide-y">
                        {candidates.length === 0 ? (
                          <p className="px-3 py-3 text-xs text-gray-400">
                            No round-robin events match.
                          </p>
                        ) : (
                          candidates.map((c) => (
                            <div
                              key={c.id}
                              className="flex items-center justify-between gap-2 px-3 py-2 text-xs"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-medium truncate">{c.name}</p>
                                <p className="text-gray-400 truncate">
                                  {c.owner_name ? `👥 ${c.owner_name}` : ""}
                                  {c.duration_minutes
                                    ? ` · ${c.duration_minutes}m`
                                    : ""}
                                </p>
                                {c.scheduling_url && (
                                  <a
                                    href={c.scheduling_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-blue-600 hover:underline text-[11px] break-all block mt-0.5"
                                  >
                                    {c.scheduling_url}
                                  </a>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={
                                  !typeSlug.trim() ||
                                  typeIsDuplicate ||
                                  !selectedTeamIds.length ||
                                  createRR.isPending
                                }
                                onClick={() => createRR.mutate(c)}
                                className="shrink-0"
                              >
                                Use{typeSlug.trim() ? ` · ${slugify(typeSlug)}` : ""}
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                      {!typeSlug.trim() && (
                        <p className="text-[11px] text-gray-400">
                          Enter a unique type to enable adding.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400">
              Round-robin pool is not active for this managed event — only
              individual professional event types apply.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
